import React, { useState, useEffect } from "react";
import { Card, Table, Tag, Typography, Select, Space, message } from "antd";
import {
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  BellOutlined,
} from "@ant-design/icons";
import api from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import dayjs from "dayjs";

const { Title } = Typography;
const { Option } = Select;

const SilenceRequests = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState(null);

  useEffect(() => {
    fetchData();
  }, [statusFilter, user?.current_company_id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter) params.status = statusFilter;

      const response = await api.get("/silence-requests/history", { params });
      if (response.data.success) {
        setRequests(response.data.data);
      }
    } catch (error) {
      console.error("Fetch silence requests error:", error);
      message.error("ไม่สามารถโหลดข้อมูลได้");
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (minutes) => {
    if (minutes >= 60) return `${minutes / 60} ชั่วโมง`;
    return `${minutes} นาที`;
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "-";
    return dayjs(dateStr).format("DD/MM/YYYY HH:mm");
  };

  const columns = [
    {
      title: "วันที่ขอ",
      dataIndex: "requested_at",
      key: "requested_at",
      render: (val) => formatDateTime(val),
      sorter: (a, b) =>
        new Date(a.requested_at) - new Date(b.requested_at),
      defaultSortOrder: "descend",
      width: 140,
    },
    {
      title: "ชื่อ รปภ",
      dataIndex: "guard_name",
      key: "guard_name",
      width: 160,
      render: (name, record) => (
        <div>
          <div>{name}</div>
          {record.guard_employee_id && (
            <div style={{ fontSize: 12, color: "#999" }}>
              {record.guard_employee_id}
            </div>
          )}
        </div>
      ),
    },
    {
      title: "บริษัท",
      dataIndex: "company_name",
      key: "company_name",
      width: 160,
      ellipsis: true,
    },
    {
      title: "เหตุผล",
      dataIndex: "reason",
      key: "reason",
      ellipsis: true,
      width: 200,
    },
    {
      title: "ระยะเวลา",
      dataIndex: "duration_minutes",
      key: "duration_minutes",
      render: (val) => formatDuration(val),
      width: 100,
    },
    {
      title: "ผู้อนุมัติ",
      dataIndex: "supervisor_name",
      key: "supervisor_name",
      width: 150,
      ellipsis: true,
    },
    {
      title: "สถานะ",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        const config = {
          pending: {
            color: "orange",
            icon: <ClockCircleOutlined />,
            text: "รออนุมัติ",
          },
          approved: {
            color: "green",
            icon: <CheckCircleOutlined />,
            text: "อนุมัติ",
          },
          rejected: {
            color: "red",
            icon: <CloseCircleOutlined />,
            text: "ปฏิเสธ",
          },
        };
        const c = config[status] || config.pending;
        return (
          <Tag color={c.color} icon={c.icon}>
            {c.text}
          </Tag>
        );
      },
      width: 110,
    },
    {
      title: "หยุดเตือนถึง",
      dataIndex: "silence_until",
      key: "silence_until",
      render: (val) => (val ? formatDateTime(val) : "-"),
      width: 140,
    },
    {
      title: "ตอบเมื่อ",
      dataIndex: "responded_at",
      key: "responded_at",
      render: (val) => (val ? formatDateTime(val) : "-"),
      width: 140,
    },
    {
      title: "หมายเหตุ",
      dataIndex: "response_note",
      key: "response_note",
      render: (val) => val || "-",
      ellipsis: true,
      width: 150,
    },
  ];

  // Summary stats
  const pendingCount = requests.filter((r) => r.status === "pending").length;
  const approvedCount = requests.filter((r) => r.status === "approved").length;
  const rejectedCount = requests.filter((r) => r.status === "rejected").length;

  return (
    <div style={{ padding: "24px" }}>
      <div
        style={{
          marginBottom: 24,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Title level={3} style={{ margin: 0 }}>
          <BellOutlined style={{ marginRight: 8 }} />
          คำขอหยุดเสียงเตือน
        </Title>
        <Space>
          <Select
            placeholder="กรองสถานะ"
            allowClear
            style={{ width: 160 }}
            value={statusFilter}
            onChange={setStatusFilter}
          >
            <Option value="pending">รออนุมัติ</Option>
            <Option value="approved">อนุมัติ</Option>
            <Option value="rejected">ปฏิเสธ</Option>
          </Select>
        </Space>
      </div>

      {/* Stats Cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 16,
          marginBottom: 24,
        }}
      >
        <Card size="small">
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 24, fontWeight: "bold", color: "#faad14" }}>
              {pendingCount}
            </div>
            <div style={{ color: "#999" }}>รออนุมัติ</div>
          </div>
        </Card>
        <Card size="small">
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 24, fontWeight: "bold", color: "#52c41a" }}>
              {approvedCount}
            </div>
            <div style={{ color: "#999" }}>อนุมัติ</div>
          </div>
        </Card>
        <Card size="small">
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 24, fontWeight: "bold", color: "#ff4d4f" }}>
              {rejectedCount}
            </div>
            <div style={{ color: "#999" }}>ปฏิเสธ</div>
          </div>
        </Card>
      </div>

      <Card>
        <Table
          columns={columns}
          dataSource={requests}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 20, showSizeChanger: true }}
          scroll={{ x: 1450 }}
          size="middle"
        />
      </Card>
    </div>
  );
};

export default SilenceRequests;
